import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.css';

/* ----- icônes inline ----- */
const Icons = {
  dashboard: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="1" width="6" height="6" rx="1.5" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" />
    </svg>
  ),
  collect: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M14 2H2a1 1 0 00-1 1v9a1 1 0 001 1h5l1 2 1-2h5a1 1 0 001-1V3a1 1 0 00-1-1z" />
    </svg>
  ),
  csv: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M8 2v8M5 7l3 3 3-3M2 12h12" />
    </svg>
  ),
  qr: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="1" width="5" height="5" rx="1" />
      <rect x="2" y="2" width="3" height="3" fill="white" />
      <rect x="10" y="1" width="5" height="5" rx="1" />
      <rect x="11" y="2" width="3" height="3" fill="white" />
      <rect x="1" y="10" width="5" height="5" rx="1" />
      <rect x="2" y="11" width="3" height="3" fill="white" />
      <rect x="10" y="10" width="2" height="2" />
      <rect x="13" y="10" width="2" height="2" />
      <rect x="10" y="13" width="2" height="2" />
      <rect x="13" y="13" width="2" height="2" />
    </svg>
  ),
  client: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <circle cx="8" cy="5" r="3" />
      <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    </svg>
  ),
  config: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.5v3.5l2.5 1.5" strokeLinecap="round" />
    </svg>
  ),
  billing: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1" y="3" width="14" height="10" rx="1.5" />
      <line x1="1" y1="6" x2="15" y2="6" />
    </svg>
  ),
  google: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1l2.2 4.5L15 6.2l-3.5 3.4.8 4.8L8 12l-4.3 2.4.8-4.8L1 6.2l4.8-.7z" />
    </svg>
  ),
  reply: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M2 3h12v8H5l-3 3V3z" />
    </svg>
  ),
  insights: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M2 11h2v3H2v-3zm3-4h2v7H5V7zm3-3h2v10H8V4zm3-2h2v12h-2V2z" />
    </svg>
  ),
  competitors: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4v4l3 2" strokeLinecap="round" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M8 1.5l5.5 2v4c0 3.2-2.3 5.6-5.5 6.5C4.8 13.1 2.5 10.7 2.5 7.5v-4l5.5-2z" strokeLinejoin="round" />
    </svg>
  ),
};

// Groupes de navigation — les `label` sont des clés i18n (ns dashboard.sidebar.*).
// Les `to` (routes) restent en FR et ne sont pas i18n-isés (cf brief P5 §5.1) :
// changer les URLs casserait les bookmarks resto et le SEO interne.
const NAV_GROUPS = [
  {
    titleKey: 'sidebar.section_gestion',
    items: [
      { to: '/dashboard',     labelKey: 'sidebar.item_dashboard',     icon: 'dashboard' },
      { to: '/collecte',      labelKey: 'sidebar.item_collecte',      icon: 'collect' }, // badge dynamique via prop
      { to: '/import',        labelKey: 'sidebar.item_import',        icon: 'csv' },
      { to: '/qr',            labelKey: 'sidebar.item_qr',            icon: 'qr' },
      { to: '/page-client',   labelKey: 'sidebar.item_page_client',   icon: 'client' },
      { to: '/configuration', labelKey: 'sidebar.item_configuration', icon: 'config' },
      { to: '/abonnement',    labelKey: 'sidebar.item_abonnement',    icon: 'billing' },
    ],
  },
  {
    titleKey: 'sidebar.section_analyse',
    items: [
      { to: '/avis',        labelKey: 'sidebar.item_avis_google', icon: 'google' },
      { to: '/repondre',    labelKey: 'sidebar.item_repondre',    icon: 'reply' },
      { to: '/insights',    labelKey: 'sidebar.item_insights',    icon: 'insights' },
      { to: '/concurrents', labelKey: 'sidebar.item_competitors', icon: 'competitors' },
      { to: '/feedbacks',   labelKey: 'sidebar.item_feedbacks',   icon: 'shield' },
    ],
  },
];

// Compat retro : si l'ancienne clé `item_competitors` n'existe pas (renommée
// vers `item_concurrents` dans dashboard.json), on retombe sur `item_concurrents`
// via le fallback explicite ci-dessous.

/**
 * Sidebar — navigation principale.
 *
 * Props :
 *  - restaurant : { name, location, emoji }
 *  - user       : { initials, name, role }
 *  - onSwitchRestaurant() · onLogout()
 */
export default function Sidebar({
  restaurant,
  user,
  badges = {},   // { '/collecte': 3 } pour override les badges dynamiquement
  isOpen = false,
  onClose,
  onSwitchRestaurant,
  onLogout,
}) {
  const { t } = useTranslation('dashboard');

  // Defaults i18n-isés (anciens defaults JS hardcodés FR)
  const safeRestaurant = restaurant || {
    name: t('sidebar.default_resto_name'),
    location: '',
    emoji: '🍽️',
  };
  const safeUser = user || {
    initials: 'FB',
    name: t('sidebar.default_user_fallback'),
    role: t('sidebar.default_user_role'),
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      <div
        className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
      <div className={styles.logoWrap}>
        <div className={styles.logoInner}>
          <div className={styles.logoMark}>R</div>
          <div>
            <div className={styles.logoName}>Revyo</div>
            <div className={styles.logoTag}>{t('sidebar.logo_tag')}</div>
          </div>
        </div>
      </div>

      <div
        className={styles.restoSwitch}
        onClick={onSwitchRestaurant}
        role="button"
        tabIndex={0}
      >
        <div className={styles.restoAvatar}>{safeRestaurant.emoji}</div>
        <div className={styles.restoInfo}>
          <div className={styles.restoName}>{safeRestaurant.name}</div>
          <div className={styles.restoLoc}>{safeRestaurant.location}</div>
        </div>
        <span className={styles.restoCaret}>⌄</span>
      </div>

      <nav className={styles.nav}>
        {NAV_GROUPS.map((group) => (
          <React.Fragment key={group.titleKey}>
            <div className={styles.navSection}>{t(group.titleKey)}</div>
            {group.items.map((item) => {
              // Fallback retro : la clé sidebar.item_competitors n'existe pas
              // dans dashboard.json (renommée vers item_concurrents). On lit
              // d'abord la clé "FR-native" (item_concurrents) avec defaultValue.
              const label = item.labelKey === 'sidebar.item_competitors'
                ? t('sidebar.item_concurrents', { defaultValue: t('sidebar.item_competitors') })
                : t(item.labelKey);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.active : ''}`
                  }
                >
                  {Icons[item.icon]}
                  {label}
                  {(() => {
                    // Priorité au badge dynamique passé en prop, sinon item.badge fixe
                    const dynamicBadge = badges[item.to];
                    const badgeValue = dynamicBadge !== undefined ? dynamicBadge : item.badge;
                    return badgeValue && badgeValue > 0
                      ? <span className={styles.navBadge}>{badgeValue}</span>
                      : null;
                  })()}
                </NavLink>
              );
            })}
          </React.Fragment>
        ))}
      </nav>

      <div className={styles.bottom}>
        {/* Le bloc utilisateur portait `cursor:pointer` et un survol depuis
            toujours, sans aucun gestionnaire de clic : l'interface promettait
            une action qui n'existait pas. Il ouvre désormais « Mon compte ». */}
        <NavLink
          to="/compte"
          className={({ isActive }) => `${styles.userRow} ${isActive ? styles.userRowActive : ''}`}
          aria-label={t('sidebar.account_aria')}
        >
          <div className={styles.userAvatar}>{safeUser.initials}</div>
          <div>
            <div className={styles.userName}>{safeUser.name}</div>
            <div className={styles.userRole}>{safeUser.role}</div>
          </div>
        </NavLink>
        <button type="button" className={styles.btn} onClick={onLogout}>
          {t('sidebar.logout_icon')} {t('sidebar.logout')}
        </button>
      </div>
    </aside>
    </>
  );
}
